import { z } from "zod";
import { listQuerySchema, timestampSchema, uuidSchema } from "./api.ts";
import { blankToNull, timestampsShape } from "./common.ts";
import { userRefSchema } from "./common.ts";

/**
 * DATA — CSV import, CSV/JSON export and the demo seed (PRD F7, §7 Item 7).
 *
 * Two jobs live here, and they share one idea: *nothing enters or leaves the
 * workspace without a receipt*. An import is a `batch` that knows which rows it
 * created, so it can be undone; an export is a `job` that knows exactly what it
 * wrote, so its totals can be reconciled against the reports the same numbers
 * came from.
 */

// ---------------------------------------------------------------------------
// Import — enumerations
// ---------------------------------------------------------------------------

/**
 * What can be imported. An entity is only *importable* when the module that
 * owns it has registered a writer (see `data/public.ts` → `registerImportWriter`);
 * `GET /imports/entities` reports which ones are live on this deployment, so the
 * UI never offers a tab that cannot finish.
 */
export const IMPORT_ENTITIES = ["companies", "contacts", "clients", "leads", "projects", "tasks", "invoices", "payments"] as const;
export type ImportEntity = (typeof IMPORT_ENTITIES)[number];
export const importEntitySchema = z.enum(IMPORT_ENTITIES);

export const IMPORT_ENTITY_LABELS: Record<ImportEntity, string> = {
  companies: "Companies",
  contacts: "Contacts",
  clients: "Clients",
  leads: "Leads",
  projects: "Projects",
  tasks: "Tasks",
  invoices: "Historic invoices",
  payments: "Payments",
};

/**
 * `create_only` never touches an existing record (a match is skipped),
 * `update` only touches existing ones (a non-match is skipped),
 * `upsert` does both — which is what makes a re-run of the same file a no-op
 * rather than a second copy of everybody.
 */
export const IMPORT_MODES = ["create_only", "update", "upsert"] as const;
export type ImportMode = (typeof IMPORT_MODES)[number];
export const importModeSchema = z.enum(IMPORT_MODES);

/** Fields a row can be matched on. Tried in the order the batch lists them. */
export const DEDUPE_KEYS = ["email", "phone", "gstin", "domain", "name", "externalId"] as const;
export type DedupeKey = (typeof DEDUPE_KEYS)[number];
export const dedupeKeySchema = z.enum(DEDUPE_KEYS);

export const DEDUPE_KEY_LABELS: Record<DedupeKey, string> = {
  email: "Email address",
  phone: "Phone number (E.164)",
  gstin: "GSTIN",
  domain: "Website domain",
  name: "Normalised name",
  externalId: "External id from the old system",
};

export const IMPORT_BATCH_STATUSES = ["pending", "running", "completed", "failed", "rolled_back"] as const;
export type ImportBatchStatus = (typeof IMPORT_BATCH_STATUSES)[number];
export const importBatchStatusSchema = z.enum(IMPORT_BATCH_STATUSES);

/** A batch may be undone for this long, and only while nothing points at its rows. */
export const IMPORT_ROLLBACK_WINDOW_DAYS = 7;
/** Hard ceiling on one file, so a runaway import cannot hold a worker for an hour. */
export const IMPORT_MAX_ROWS = 20_000;

// ---------------------------------------------------------------------------
// Import — DTOs
// ---------------------------------------------------------------------------

/** One thing that was wrong with one row, at the column that caused it. */
export const importRowErrorSchema = z.object({
  /** 1-based, counting the header as row 1 — the number the spreadsheet shows. */
  row: z.number().int(),
  column: z.string().nullable(),
  message: z.string(),
});
export type ImportRowErrorDto = z.infer<typeof importRowErrorSchema>;

export const importCountsSchema = z.object({
  total: z.number().int(),
  processed: z.number().int(),
  created: z.number().int(),
  updated: z.number().int(),
  skipped: z.number().int(),
  failed: z.number().int(),
});
export type ImportCountsDto = z.infer<typeof importCountsSchema>;

/** GET /api/v1/imports/:id (perm `data:import`). */
export const importBatchSchema = z.object({
  id: uuidSchema,
  entity: importEntitySchema,
  mode: importModeSchema,
  status: importBatchStatusSchema,
  dryRun: z.boolean(),
  filename: z.string(),
  /** header → target field; a header with no target was ignored. */
  mapping: z.record(z.string(), z.string()),
  dedupeOn: z.array(dedupeKeySchema),
  counts: importCountsSchema,
  /** At most the first 200 problems — the rest are counted, not listed. */
  errors: z.array(importRowErrorSchema),
  /** Percent, 0–100, for the progress bar while the job runs. */
  progress: z.number().int(),
  startedAt: timestampSchema.nullable(),
  finishedAt: timestampSchema.nullable(),
  rolledBackAt: timestampSchema.nullable(),
  /** Until when this batch may still be undone; null once it has been. */
  rollbackUntil: timestampSchema.nullable(),
  createdBy: userRefSchema.nullable(),
  ...timestampsShape,
});
export type ImportBatchDto = z.infer<typeof importBatchSchema>;

/** One detected column of the uploaded file, with the target we think it means. */
export const importColumnSchema = z.object({
  header: z.string(),
  /** First non-empty value, so a human can recognise the column. */
  sample: z.string().nullable(),
  suggestedField: z.string().nullable(),
});
export type ImportColumnDto = z.infer<typeof importColumnSchema>;

/** A field an entity accepts, for the mapping UI. */
export const importFieldSchema = z.object({
  field: z.string(),
  label: z.string(),
  required: z.boolean(),
  /** Which dedupe key (if any) this field feeds. */
  dedupeKey: dedupeKeySchema.nullable(),
  example: z.string().nullable(),
});
export type ImportFieldDto = z.infer<typeof importFieldSchema>;

/** POST /api/v1/imports/preview (perm `data:import`) → what the file looks like. */
export const importPreviewSchema = z.object({
  entity: importEntitySchema,
  filename: z.string(),
  rowCount: z.number().int(),
  columns: z.array(importColumnSchema),
  fields: z.array(importFieldSchema),
  /** The first few parsed rows, mapped — what the user will confirm. */
  sampleRows: z.array(z.record(z.string(), z.string())),
  truncated: z.boolean(),
});
export type ImportPreviewDto = z.infer<typeof importPreviewSchema>;

/** GET /api/v1/imports/entities (perm `data:import`). */
export const importEntityInfoSchema = z.object({
  entity: importEntitySchema,
  label: z.string(),
  /** False until the owning module registers its writer. */
  available: z.boolean(),
  fields: z.array(importFieldSchema),
  dedupeKeys: z.array(dedupeKeySchema),
});
export type ImportEntityInfoDto = z.infer<typeof importEntityInfoSchema>;

/** A reusable column map, so the same monthly export from the old CRM is one click. */
export const importMappingSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  entity: importEntitySchema,
  mapping: z.record(z.string(), z.string()),
  dedupeOn: z.array(dedupeKeySchema),
  mode: importModeSchema,
  lastUsedAt: timestampSchema.nullable(),
  ...timestampsShape,
});
export type ImportMappingDto = z.infer<typeof importMappingSchema>;

// ---------------------------------------------------------------------------
// Import — inputs
// ---------------------------------------------------------------------------

const csvBodySchema = z.string().min(1).max(8_000_000);

/** POST /api/v1/imports/preview (perm `data:import`) → ImportPreview. */
export const importPreviewInputSchema = z
  .object({
    entity: importEntitySchema,
    filename: z.string().trim().min(1).max(200),
    content: csvBodySchema,
    mapping: z.record(z.string(), z.string()).optional(),
  })
  .strict();
export type ImportPreviewInput = z.input<typeof importPreviewInputSchema>;

/**
 * POST /api/v1/imports (perm `data:import`) → 201 ImportBatch.
 *
 * `dryRun: true` (the default) validates and matches every row and reports what
 * WOULD happen without writing anything — the step the PRD makes mandatory
 * before a real import. Send `dryRun: false` to commit.
 */
export const createImportInputSchema = z
  .object({
    entity: importEntitySchema,
    filename: z.string().trim().min(1).max(200),
    content: csvBodySchema,
    /** header → field. Omit to reuse a saved mapping, or to accept the suggestions. */
    mapping: z.record(z.string(), z.string()).optional(),
    mappingId: uuidSchema.optional(),
    mode: importModeSchema.optional(),
    dedupeOn: z.array(dedupeKeySchema).max(DEDUPE_KEYS.length).optional(),
    dryRun: z.boolean().optional(),
    /** Save the mapping under this name for next time. */
    saveMappingAs: z.string().trim().min(1).max(80).optional(),
  })
  .strict();
export type CreateImportInput = z.input<typeof createImportInputSchema>;

export const listImportsQuerySchema = listQuerySchema.extend({
  entity: importEntitySchema.optional(),
  status: importBatchStatusSchema.optional(),
});
export type ListImportsQuery = z.infer<typeof listImportsQuerySchema>;

/** POST /api/v1/imports/mappings (perm `data:import`) → 201 ImportMapping. */
export const saveImportMappingInputSchema = z
  .object({
    name: z.string().trim().min(1).max(80),
    entity: importEntitySchema,
    mapping: z.record(z.string(), z.string()),
    dedupeOn: z.array(dedupeKeySchema).max(DEDUPE_KEYS.length).optional(),
    mode: importModeSchema.optional(),
  })
  .strict();
export type SaveImportMappingInput = z.input<typeof saveImportMappingInputSchema>;

/**
 * POST /api/v1/imports/:id/rollback (perm `data:import`) → ImportBatch.
 * Refused after `IMPORT_ROLLBACK_WINDOW_DAYS`, and refused row by row for
 * anything an issued financial document now points at (PRD F7).
 */
export const rollbackImportResultSchema = z.object({
  batch: importBatchSchema,
  removed: z.number().int(),
  /** Rows that were kept, with the reason a human can act on. */
  kept: z.array(z.object({ entityId: uuidSchema, reason: z.string() })),
});
export type RollbackImportResultDto = z.infer<typeof rollbackImportResultSchema>;

// ---------------------------------------------------------------------------
// Export
// ---------------------------------------------------------------------------

/** A list view that can be exported to CSV, honouring the filters on screen. */
export const EXPORT_DATASETS = ["clients", "companies", "contacts", "engagements", "projects", "tasks", "invoices", "payments", "ageing"] as const;
export type ExportDataset = (typeof EXPORT_DATASETS)[number];
export const exportDatasetSchema = z.enum(EXPORT_DATASETS);

export const EXPORT_JOB_STATUSES = ["pending", "running", "completed", "failed", "expired"] as const;
export type ExportJobStatus = (typeof EXPORT_JOB_STATUSES)[number];
export const exportJobStatusSchema = z.enum(EXPORT_JOB_STATUSES);

/** A signed workspace-export link is good for this long (PRD F7). */
export const EXPORT_LINK_TTL_HOURS = 24;

/** The filters a list export carries over from the screen it was started from. */
export const exportFiltersSchema = z
  .object({
    clientId: uuidSchema.nullable().optional(),
    status: z.array(z.string().max(40)).max(20).optional(),
    from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
    to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
    q: blankToNull(z.string().max(200)).optional(),
    includeArchived: z.boolean().optional(),
  })
  .strict();
export type ExportFilters = z.infer<typeof exportFiltersSchema>;

/**
 * POST /api/v1/exports/list (perm `data:export`) → the CSV, inline.
 * Money columns are omitted for a caller without `financials:view`.
 */
export const exportListInputSchema = z
  .object({
    dataset: exportDatasetSchema,
    filters: exportFiltersSchema.optional(),
  })
  .strict();
export type ExportListInput = z.input<typeof exportListInputSchema>;

/** What a list export produced — the same numbers the report shows, plus a checksum. */
export const exportResultSchema = z.object({
  dataset: exportDatasetSchema,
  filename: z.string(),
  contentType: z.string(),
  rowCount: z.number().int(),
  columns: z.array(z.string()),
  /** Column → summed minor units, for the reconciliation check against reports. */
  totals: z.record(z.string(), z.number()),
  content: z.string(),
});
export type ExportResultDto = z.infer<typeof exportResultSchema>;

/** GET /api/v1/exports/:id (perm `data:export`). */
export const exportJobSchema = z.object({
  id: uuidSchema,
  kind: z.enum(["workspace", "list"]),
  dataset: exportDatasetSchema.nullable(),
  status: exportJobStatusSchema,
  progress: z.number().int(),
  /** Every file in the bundle, with its row count. */
  files: z.array(z.object({ path: z.string(), rowCount: z.number().int(), sizeBytes: z.number().int() })),
  sizeBytes: z.number().int(),
  error: z.string().nullable(),
  /** Signed, unauthenticated, valid for 24 h. Null until the job finishes. */
  downloadUrl: z.string().nullable(),
  expiresAt: timestampSchema.nullable(),
  requestedBy: userRefSchema.nullable(),
  ...timestampsShape,
});
export type ExportJobDto = z.infer<typeof exportJobSchema>;

/**
 * POST /api/v1/exports/workspace (perm `data:export`) → 202 ExportJob.
 * Produces JSON + CSV + the file manifest as one bundle, in the background,
 * behind a signed link that expires after 24 hours.
 */
export const createWorkspaceExportInputSchema = z
  .object({
    /** Leave empty for everything. */
    datasets: z.array(exportDatasetSchema).max(EXPORT_DATASETS.length).optional(),
    includeFiles: z.boolean().optional(),
    /**
     * The caller's own password, re-entered.
     *
     * A full export is every client, contact, invoice and file the workspace
     * has, in one download behind a link that works for 24 hours. It is the
     * single most valuable action an attacker with a borrowed session can
     * take, and the one a departing employee reaches for.
     */
    password: z.string().min(1).max(200),
    totpCode: z.string().min(6).max(10).optional(),
  })
  .strict();
export type CreateWorkspaceExportInput = z.input<typeof createWorkspaceExportInputSchema>;

export const listExportsQuerySchema = listQuerySchema.extend({
  status: exportJobStatusSchema.optional(),
});
export type ListExportsQuery = z.infer<typeof listExportsQuerySchema>;

/** GET /api/v1/exports/:id/download?expires&sig — the signed, unauthenticated link. */
export const signedExportQuerySchema = z.object({
  expires: z.coerce.number().int(),
  sig: z.string().min(1).max(200),
});
export type SignedExportQuery = z.infer<typeof signedExportQuerySchema>;

// ---------------------------------------------------------------------------
// Demo seed
// ---------------------------------------------------------------------------

/** What `npm run db:seed` reports, and what `GET /api/v1/demo-seed` would show. */
export const seedSummarySchema = z.object({
  orgId: uuidSchema,
  orgName: z.string(),
  gstin: z.string(),
  alreadySeeded: z.boolean(),
  counts: z.record(z.string(), z.number()),
});
export type SeedSummaryDto = z.infer<typeof seedSummarySchema>;

// ---------------------------------------------------------------------------
// Spreadsheet formula injection (CWE-1236)
// ---------------------------------------------------------------------------

const FORMULA_TRIGGER = /^[=+\-@\t\r\uFF1D\uFF0B\uFF0D\uFF20]/;
const PLAIN_NUMBER = /^[+-]?\d+(?:\.\d+)?$/;

/**
 * The one rule every CSV we hand a person must follow. Excel, LibreOffice and
 * Google Sheets evaluate a cell that STARTS with `=`, `+`, `-` or `@` (and, in
 * some versions, a tab, a carriage return or the full-width forms of those
 * signs) as a formula — CSV quoting does not stop them. A client's legal name,
 * a contact's name, an uploaded filename, a proof comment or a credit note's
 * reason can be written by somebody outside the agency, and the file lands in
 * an accountant's spreadsheet: `=HYPERLINK(...)` leaks the sheet, a DDE
 * payload asks to run a program.
 *
 * Such a cell is prefixed with an apostrophe, which every spreadsheet reads as
 * "this is text". A plain number (`-1180.00`, a credit note's negative value)
 * is left alone: it cannot be a formula, and the accountant must be able to
 * sum it. Apply it to the cell's TEXT, before CSV quoting.
 */
export function neutralizeSpreadsheetFormula(text: string): string {
  if (!FORMULA_TRIGGER.test(text) || PLAIN_NUMBER.test(text)) return text;
  return `'${text}`;
}
